import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  PopoverActions,
  Typography
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useDebounce } from '@uidotdev/usehooks';
import { Button } from 'system/atoms';
import { SearchInput } from 'common/ui/SearchInput';
import {
  CheckboxCheckedIcon,
  CheckboxIcon
} from 'common/form/components/Checkbox';
import { useSearchCandidateTagsLazyQuery } from 'generated-graphql/hooks/apollo-hooks';
import { isTagSelected } from '../utils/isTagSelected';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { logger } from 'core/utils/logger';
import { buildTagCatalogFilter } from '../utils/buildTagCatalogFilter';
import {
  buildTagFilterOptions,
  MAX_VISIBLE_TAGS
} from '../utils/buildTagFilterOptions';
import { conversationTagsSx } from '../styles';

type Props = {
  organizationId: string;
  selectedTags: string[];
  onToggleTag: (tag: string) => void;
  onClearTags: () => void;
};

const PANEL_SCREEN_MARGIN_PX = 16;
const MIN_PANEL_HEIGHT_PX = 380;

/**
 * One flat list: the query ORs every selected tag. Ticks wait until Apply or
 * until the menu closes.
 */
export const ConversationTagsFilterButton: React.FC<Props> = ({
  organizationId,
  selectedTags,
  onToggleTag,
  onClearTags
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stagedTags, setStagedTags] = useState<string[]>(selectedTags);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [searchCandidateTags, { data, loading }] =
    useSearchCandidateTagsLazyQuery({
      fetchPolicy: 'cache-and-network',
      onError: (error) =>
        logger.error('Failed to fetch tags for filter', {
          error,
          query: debouncedSearchQuery
        })
    });

  useEffect(() => {
    if (!open) {
      return;
    }

    searchCandidateTags({
      variables: {
        limit: MAX_VISIBLE_TAGS,
        filter: buildTagCatalogFilter(organizationId, debouncedSearchQuery)
      }
    });
  }, [open, debouncedSearchQuery, organizationId, searchCandidateTags]);

  // A tag ticked while it is listed keeps its place, so the next click lands
  // where the recruiter aims. A tick the search no longer matches joins the
  // applied tags at the top, so it can still be seen and unticked.
  const tags = useMemo(() => {
    const matchedTags = data
      ? extractQueryListItems(data).map(({ value }) => value)
      : [];
    const hiddenTicks = stagedTags.filter(
      (tag) =>
        !isTagSelected(selectedTags, tag) && !isTagSelected(matchedTags, tag)
    );

    return buildTagFilterOptions({
      matchedTags,
      selectedTags: [...selectedTags, ...hiddenTicks]
    });
  }, [data, selectedTags, stagedTags]);

  const hasResults = tags.length > 0;

  const [panelMaxHeight, setPanelMaxHeight] = useState<number>();

  // With less room than the minimum below the header, the panel grows past
  // the screen once the tags load, so MUI has to place it again.
  const popoverActions = useRef<PopoverActions>(null);
  useEffect(() => {
    popoverActions.current?.updatePosition();
  }, [tags.length, loading]);

  const toggleStagedTag = (tag: string) =>
    setStagedTags((previous) =>
      isTagSelected(previous, tag)
        ? previous.filter((staged) => !isTagSelected([staged], tag))
        : [...previous, tag]
    );

  const changedTags = [
    ...stagedTags.filter((tag) => !isTagSelected(selectedTags, tag)),
    ...selectedTags.filter((tag) => !isTagSelected(stagedTags, tag))
  ];

  // Closing keeps what was ticked, as Apply does, like the other table filters.
  const applyAndClose = () => {
    changedTags.forEach((tag) => onToggleTag(tag));
    setOpen(false);
  };

  const clearAndClose = () => {
    onClearTags();
    setStagedTags([]);
    setOpen(false);
  };

  return (
    <Box>
      <Popover
        action={popoverActions}
        sx={conversationTagsSx.filterPopover}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={applyAndClose}
      >
        <Box sx={conversationTagsSx.filterPanel(panelMaxHeight)}>
          <SearchInput placeholder="Search tags" onChange={setSearchQuery} />

          {loading && !hasResults && (
            <Box sx={conversationTagsSx.filterMessage}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!loading && !hasResults && (
            <Typography sx={conversationTagsSx.filterMessage}>
              {searchQuery
                ? `No tags matching '${searchQuery}'`
                : 'No tags yet'}
            </Typography>
          )}

          <Box sx={conversationTagsSx.filterList} data-tag-list>
            {tags.map((tag) => (
              <FormControlLabel
                key={tag}
                sx={conversationTagsSx.filterOption}
                control={
                  <Checkbox
                    icon={<CheckboxIcon />}
                    checkedIcon={<CheckboxCheckedIcon />}
                    data-testid={`tag-filter-${tag}`}
                    checked={isTagSelected(stagedTags, tag)}
                    onChange={() => toggleStagedTag(tag)}
                  />
                }
                label={
                  <Typography variant="body2" noWrap>
                    {tag}
                  </Typography>
                }
              />
            ))}
          </Box>

          <Button
            variant="primary"
            fullWidth
            data-testid="tag-filter-apply"
            onClick={applyAndClose}
          >
            Apply
          </Button>
          <Button
            variant="tertiary"
            fullWidth
            data-testid="tag-filter-clear"
            onClick={clearAndClose}
          >
            Clear
          </Button>
        </Box>
      </Popover>
      <Box position="relative">
        <IconButton
          onClick={(event) => {
            // The whole column, not only its icon, so the header meets the
            // table's edge instead of stopping short under the fade.
            const anchor = event.currentTarget;
            (
              anchor.closest<HTMLElement>('.rdt_TableCol') ?? anchor
            ).scrollIntoView({ block: 'nearest', inline: 'end' });
            setPanelMaxHeight(
              Math.max(
                window.innerHeight -
                  anchor.getBoundingClientRect().bottom -
                  PANEL_SCREEN_MARGIN_PX,
                MIN_PANEL_HEIGHT_PX
              )
            );
            setAnchorEl(anchor);
            setStagedTags(selectedTags);
            setOpen(true);
          }}
          size="small"
        >
          <FilterListIcon fontSize="small" sx={conversationTagsSx.filterIcon} />
        </IconButton>
      </Box>
    </Box>
  );
};
